import {
  DataTableDataSearchDto,
  SortColumnDto,
  TableCacheKeyDto,
  TableDataDtoResponse,
} from '@asset-metrix/portal-gateway-client-js';
import { QueryFunction } from '@tanstack/react-query';
import uniqBy from 'lodash/uniqBy';

import { extractErrorMessage } from '../../utils/errorMessage';
import { HttpError } from '../../utils/errors';
import {
  QueryKeyWithRole,
  unwrapQueryKeyWithRole,
} from '../../utils/prependRoleId';
import { portalGatewayClient } from '../portalGatewayClient';

export type FetchDatatableDataQueryKeyParams = {
  page: number;
  pageSize: number;
  pageFilter: DataTableDataSearchDto['selection'];
  tableFilter: DataTableDataSearchDto['selection'];
  workflowStateSelection?: DataTableDataSearchDto['workflowStateSelection'];
  sortItems: SortColumnDto[];
};

export type FetchDatatableDataQueryKey = readonly [
  TableCacheKeyDto,
  { pageId: string },
  { widgetId: string },
  FetchDatatableDataQueryKeyParams,
  ...string[],
];

export const fetchDatatableData: QueryFunction<
  TableDataDtoResponse,
  QueryKeyWithRole<FetchDatatableDataQueryKey>
> = async ({ queryKey }) => {
  const unwrappedQueryKey = unwrapQueryKeyWithRole(queryKey).params;
  const { widgetId } = unwrappedQueryKey[2];
  const {
    page,
    pageSize,
    pageFilter,
    tableFilter,
    workflowStateSelection,
    sortItems,
  } = unwrappedQueryKey[3];

  const filterSelections = {
    ...pageFilter,
    ...tableFilter,
  };

  const sort = convertSortItemsToSortString(sortItems);
  const response = await portalGatewayClient.table.searchTableData(
    widgetId,
    {
      offset: page * pageSize,
      limit: pageSize,
      ...(sort != null && { sort }),
    },
    { selection: filterSelections, workflowStateSelection }
  );
  if (!response.ok) {
    throw new HttpError(extractErrorMessage(response.error), response.status);
  }

  return response.data;
};

/**
 * @param {SortColumnDto[]} sortItems - e.g. [{key: 'status_published', sort: 'asc'},{key: 'file_name', sort: 'asc'},{key: 'created_at', sort: 'desc'}]
 * @returns {string} sortStr - A sort string, e.g. status_published,asc;file_name,asc;created_at,desc
 */
function convertSortItemsToSortString(
  sortItems: SortColumnDto[]
): string | undefined {
  if (sortItems.length === 0) {
    return undefined;
  }

  return uniqBy(sortItems, 'key')
    .map(({ key, sort }) => `${key},${sort}`)
    .join(';');
}
